<script setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRestaurantsStore } from '@/stores'
import { useRestaurantNotes } from '@/composables/useRestaurantNotes'
import RestaurantTitleBlock from '@core/components/RestaurantTitleBlock.vue'
import FriendsLikedChips from '@core/components/FriendsLikedChips.vue'

// Plain list row for AI-chat replies that list restaurants (Trending, Just
// Booked, your network's favorites, resolved OpenAI suggestions).
//
// Kapil, 2026-09-15: "get them the information as fast as they can — show them
// the list." So the row is the shared identity cluster, dense: name (17px)
// with the heart + count, restaurant group, Preferred, then who in the
// member's network favorited it (that is the relevance signal — Kapil,
// 2026-09-15). No photo, no chevron, no action line, no hairline between
// rows — the heart already adds a favorite, and tapping the row opens the
// restaurant. The only other extra is a member's own note, when they have
// one: the row then reads as THEIR place, not just another entry.
// The city shows only when the parent says the reply spans several markets.
// A single-restaurant reply still renders the full RestaurantGridCard.

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  showMarket: {
    type: Boolean,
    default: false,
  },
  // My Favorites (RM-296) renders its own note line (tappable to edit) plus booking
  // context through the `meta` slot, so the row itself stays the shared identity cluster.
  hideNote: {
    type: Boolean,
    default: false,
  },
  // The network line's label: RM AI's Discovery cards pass "Favorited by you/your network" (10/20).
  favoritedByLabel: {
    type: String,
    default: 'Favorited by',
  },
  // The chat keeps rows near the text column; a full-width list (Favorites) relaxes that.
  wide: {
    type: Boolean,
    default: false,
  },
  // My Favorites is a list page, not a chat bubble: the name takes the full 20px/23px
  // type tier the Discover rows use, instead of the chat's dense 17px (Founder review, 2026-09-17).
  fullSize: {
    type: Boolean,
    default: false,
  },
})

const restaurantsStore = useRestaurantsStore()
const { viewingDetailsRestaurant, viewingDetailsRestaurantTab, favoriteNotes } = storeToRefs(restaurantsStore)

const raw = computed(() => props.item.raw ?? props.item)
const restaurantId = computed(() => raw.value._id || raw.value.id)

// The member's private note (loaded with the user, keyed by restaurant id).
// Shown only on a place they have favorited — a note survives an unfavorite
// only briefly, and the row should not read as theirs once the heart is off.
// The payload snapshot was built when the tool ran; the member may have favorited the
// place later in the same turn. Prefer live store state (Founder review, 2026-09-17).
const favorited = computed(() => {
  const id = restaurantId.value
  if (id) {
    const live = (restaurantsStore.restaurants || []).find(r => String(r?._id) === String(id) || String(r?.id) === String(id))
    if (live && typeof live.isFavorited === 'boolean') return live.isFavorited
  }

  return !!raw.value.isFavorited
})
// Notes on this row: the newest without the restaurant's own name, and a count
// that opens the rest in place (shared with the Discover cards).
const rowName = computed(() => raw.value.name)
const { noteText: memberNote } = useRestaurantNotes(restaurantId, rowName)

// The host can render its own note line (My Favorites does); then this row shows none.
const noteText = computed(() => (
  !props.hideNote && favorited.value ? memberNote.value : ''
))

// "doesn't take inquiries via RM" for a restaurant that has opted out; '' otherwise.
// The words come with the card, from the RM AI replies table (RM-384).
const optedOutNote = computed(() => (props.item.optedOutNote ?? raw.value.optedOutNote) || '')

const titleBlockRef = ref(null)

function openDetails() {
  viewingDetailsRestaurantTab.value = 'Details'
  // Open the live record when the store has it — the chat payload is a snapshot from
  // tool time and can be missing a favorite or note written later in the turn.
  const id = restaurantId.value
  const live = id
    ? (restaurantsStore.restaurants || []).find(r => String(r?._id) === String(id) || String(r?.id) === String(id))
    : null

  viewingDetailsRestaurant.value = live || raw.value
}

function openFansDrawer() {
  titleBlockRef.value?.openFavoritedBy()
}
</script>

<template>
  <div
    class="agent-restaurant-row"
    :class="{ 'agent-restaurant-row--wide': wide, 'agent-restaurant-row--full-size': fullSize }"
    role="button"
    tabindex="0"
    @click="openDetails"
    @keydown.enter="openDetails"
  >
    <!-- THE shared identity cluster — one component on every restaurant row -->
    <RestaurantTitleBlock
      ref="titleBlockRef"
      :restaurant="raw"
      :name="item.name ?? raw.name"
      :market="item.market ?? raw.market"
      :show-market="showMarket"
      :group="(item.group ?? raw.group) || ''"
      :status="(item.status ?? raw.status) || ''"
      :inline-note="optedOutNote"
      :compact="!fullSize"
      :dense="!fullSize"
      @name-click="openDetails"
    />

    <!--
      Lines that belong with the restaurant's own facts, above the network line
      (Kapil, 2026-09-18: booking context sits above "Favorited by")
    -->
    <slot name="signals" />

    <!-- Who in your network favorited it -->
    <div
      v-if="(item.friendsWhoFavorited ?? raw.friendsWhoFavorited)?.length"
      class="agent-restaurant-row__favby"
      @click.stop="openFansDrawer"
    >
      <FriendsLikedChips
        :restaurant-id="raw._id || raw.id"
        :friends="item.friendsWhoFavorited ?? raw.friendsWhoFavorited"
        :label="favoritedByLabel"
      />
    </div>

    <!-- Your own note, when you have one -->
    <p
      v-if="noteText"
      class="agent-restaurant-row__note"
    >
      <VIcon
        icon="mdi-note-text"
        size="13"
        color="primary-dark"
      />
      <span>{{ noteText }}</span>
    </p>

    <!-- Extra lines the host adds on the same metadata scale (My Favorites: note + booking) -->
    <slot name="meta" />
  </div>
</template>

<style scoped>
/* Plain list row on the flat background — spacing alone separates rows; the
   title block's own lines (name + heart, group, Preferred, city) stack inside */
.agent-restaurant-row {
  display: flex;
  flex-direction: column;
  padding: 8px 2px;
  cursor: pointer;
  min-width: 0;
  /* Keep the row near the text on wide screens */
  max-width: 460px;
}

.agent-restaurant-row:focus-visible {
  /* Keyboard focus has to be visible — the row is the tap target for the restaurant */
  outline: 2px solid var(--ds-brand-primary-dark);
  outline-offset: 2px;
  border-radius: 4px;
}

.agent-restaurant-row--wide {
  max-width: none;
}

/* In a list (My Favorites) the row wrapper owns the padding and the hairline, so the
   row itself must not add its own on top — that made rows ~60% taller than Discover. */
.agent-restaurant-row--full-size {
  padding: 0;
}

.agent-restaurant-row__favby {
  min-width: 0;
  margin-top: 2px;
}

/* The member's note — same 13px metadata scale as the card's note line,
   readable ink, wraps fully */
.agent-restaurant-row__note {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ds-text-body);
  white-space: normal;
  word-break: break-word;
}

/* Keep the lines the member typed, but cap a note at three of them so one long
   note cannot own the row (Kapil, 2026-09-18). */
.agent-restaurant-row__note span {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

.agent-restaurant-row__note .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}
</style>
